import {
  ITicketInsightSentiment,
  ITicketInsightUrgencyBucket,
  TicketAiSentiment,
} from "../../../models/Tickets/TicketInsight";
import { BarRow, Card, EmptyState, StackedBar } from "./InsightWidgets";
import {
  sentimentBarClassMap,
  sentimentLabels,
  SentimentPill,
  urgencyBarClassMap,
  urgencyLabels,
} from "./insightMeta";

interface SentimentUrgencyCardProps {
  sentiment: ITicketInsightSentiment;
  urgency: ITicketInsightUrgencyBucket[];
}

/** Tone and urgency as the model read them from the customer's own words. */
export default function SentimentUrgencyCard({
  sentiment,
  urgency,
}: SentimentUrgencyCardProps) {
  const sentimentTotal =
    sentiment.negative + sentiment.neutral + sentiment.positive;
  const urgencyMax = Math.max(0, ...urgency.map((u) => u.count));

  const sentimentRows: { key: TicketAiSentiment; value: number }[] = [
    { key: TicketAiSentiment.Negative, value: sentiment.negative },
    { key: TicketAiSentiment.Neutral, value: sentiment.neutral },
    { key: TicketAiSentiment.Positive, value: sentiment.positive },
  ];

  return (
    <Card
      title="Sentiment & urgency"
      description="Read from the ticket text, not from the priority somebody picked."
    >
      {sentimentTotal === 0 ? (
        <EmptyState>No analysed tickets in this scope yet.</EmptyState>
      ) : (
        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <div className="mb-2 flex items-baseline justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Customer tone
              </p>
              <p className="text-xs text-gray-600">
                <span className="font-semibold text-rose-700">
                  {sentiment.activeNegativePercent}%
                </span>{" "}
                of active tickets are negative
              </p>
            </div>
            <StackedBar
              className="mb-3 h-3"
              segments={sentimentRows.map((r) => ({
                key: sentimentLabels[r.key],
                value: r.value,
                className: sentimentBarClassMap[r.key],
              }))}
            />
            <div className="space-y-1.5">
              {sentimentRows.map((r) => (
                <BarRow
                  key={r.key}
                  label={<SentimentPill sentiment={r.key} />}
                  value={r.value}
                  max={sentimentTotal}
                  barClassName={sentimentBarClassMap[r.key]}
                  valueLabel={
                    <span>
                      {r.value}{" "}
                      <span className="text-xs text-gray-500">
                        ({Math.round((r.value / sentimentTotal) * 100)}%)
                      </span>
                    </span>
                  }
                />
              ))}
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Urgency (1 can wait → 5 business-stopping)
            </p>
            <div className="space-y-1.5">
              {[...urgency].reverse().map((u) => (
                <BarRow
                  key={u.urgency}
                  label={
                    <span className="flex items-center gap-2">
                      <span className="w-3 font-semibold tabular-nums text-slate-800">
                        {u.urgency}
                      </span>
                      <span className="text-xs text-gray-600">
                        {urgencyLabels[u.urgency]}
                      </span>
                    </span>
                  }
                  value={u.count}
                  max={urgencyMax}
                  barClassName={urgencyBarClassMap[u.urgency]}
                  valueLabel={
                    <span>
                      {u.count}
                      {u.active > 0 ? (
                        <span className="text-xs text-gray-500">
                          {" "}
                          ({u.active} active)
                        </span>
                      ) : null}
                    </span>
                  }
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
